/**
 * Deep links into a map app.
 *
 * There is no web API for "which map app does this user prefer" — iOS never
 * exposes its default, and no platform lets a page enumerate installed apps
 * (`navigator.getInstalledRelatedApps()` only reports apps tied to our own
 * domain). So we always offer both and let the user pick per tap.
 *
 * Both links are https universal links rather than custom schemes (`maps://`,
 * `comgooglemaps://`): a user without the app lands on the web map instead of
 * a dead link, which is what lets us skip install detection entirely.
 *
 * Links drop a pin rather than starting turn-by-turn navigation. A directions
 * URL needs the map app's own location permission to route, and without it the
 * user lands on a "set a starting point" screen instead of the destination.
 */

export type MapsApp = 'apple' | 'google';

/** Structural subset of `Address` / `OrderLocation.listingLocation`. */
export interface MapsDestination {
  lat?: number;
  lon?: number;
  street1?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
}

/**
 * What we hand the map app: the formatted street address when we have one,
 * else raw coordinates. `street2` is deliberately excluded — apt/unit numbers
 * confuse geocoders, and every caller renders it separately anyway.
 */
export function mapsQuery(dest: MapsDestination): string | null {
  if (dest.street1) {
    return [dest.street1, dest.city, dest.stateProvince, dest.postalCode]
      .filter(Boolean)
      .join(', ');
  }
  if (dest.lat != null && dest.lon != null) return `${dest.lat},${dest.lon}`;
  return null;
}

/**
 * Universal link for `app`, or null when `dest` has neither a street nor
 * coordinates. `label` names the pin in the coordinates-only case.
 */
export function buildMapsUrl(
  app: MapsApp,
  dest: MapsDestination,
  label?: string,
): string | null {
  const query = mapsQuery(dest);
  if (!query) return null;
  const encoded = encodeURIComponent(query);

  if (app === 'google') {
    return `https://www.google.com/maps/search/?api=1&query=${encoded}`;
  }

  // Apple reads `q` as a search term on its own, but as the pin's LABEL when
  // `ll` is present — so a coordinates-only destination pins exactly where we
  // say instead of being re-geocoded from its own coordinate string.
  if (!dest.street1 && dest.lat != null && dest.lon != null) {
    return `https://maps.apple.com/?ll=${dest.lat},${dest.lon}&q=${encodeURIComponent(label || query)}`;
  }
  return `https://maps.apple.com/?q=${encoded}`;
}
